import { useFilePreview } from '@/contexts/file-preview'
import { usePresignedGetUrl } from '@/hooks/usePresignedGetUrl'
import { Attachment } from '@vnetwork/datacentral-core/domain'
import { downloadFile } from '@/utils/function'
import { useCallback } from 'react'
import { toast } from '@vnetwork/datacentral-ui/feedback'
import { useTranslation } from 'react-i18next'

// Office file extensions that need encodeURIComponent
const OFFICE_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']

/**
 * Hook for handling file preview and download with proper URL encoding for Office documents
 */
export const useFilePreviewWithPresigned = () => {
    const { t } = useTranslation()
    const { getPresignedUrl } = usePresignedGetUrl()
    const { preview } = useFilePreview()

    /**
     * Get file extension from attachment
     */
    const getFileExtension = useCallback((attachment: Attachment): string => {
        const fileName =
            attachment?.attachment_file_name ||
            attachment?.attachment_file_url ||
            ''
        return fileName.split('.').pop()?.toLowerCase() || ''
    }, [])

    /**
     * Check if file is an Office document that needs URL encoding
     */
    const isOfficeFile = useCallback(
        (attachment: Attachment): boolean => {
            const extension = getFileExtension(attachment)
            return OFFICE_EXTENSIONS.includes(extension)
        },
        [getFileExtension]
    )

    /**
     * Open file preview with proper URL handling. `options.allowDownload: false`
     * = view-only viewer (data types that forbid download, e.g. Regulations).
     */
    const openFilePreview = useCallback(
        async (
            attachment: Attachment,
            options?: { allowDownload?: boolean }
        ) => {
            const path = attachment?.attachment_file_url
            if (!path) {
                toast.error(t('not-found-file-path'))
                return
            }

            try {
                const presignedUrl = await getPresignedUrl(path)

                if (!presignedUrl) {
                    throw new Error(t('error-getting-file'))
                }

                const fileName =
                    attachment?.attachment_file_name ||
                    path.split('/').pop() ||
                    'file'

                // The viewer embeds Office files itself (it encodes the URL).
                preview(
                    presignedUrl,
                    undefined,
                    {
                        attachment_file_name: fileName,
                        attachment_file_url: presignedUrl,
                        attachment_file_type: attachment?.attachment_file_type,
                        attachment_file_size: attachment?.attachment_file_size,
                    },
                    undefined,
                    {
                        ...options,
                        refreshUrl: async () =>
                            (await getPresignedUrl(path)) || undefined,
                    }
                )
            } catch (error) {
                console.error('Failed to get presigned URL:', error)
                toast.error(t('error-getting-file'))
            }
        },
        [getPresignedUrl, preview, t, isOfficeFile]
    )

    /**
     * Download file with proper URL handling
     */
    const downloadFileWithPresigned = useCallback(
        async (attachment: Attachment) => {
            const path = attachment?.attachment_file_url
            if (!path) {
                toast.error(t('not-found-file-path'))
                return
            }

            try {
                const presignedUrl = await getPresignedUrl(path)

                if (!presignedUrl) {
                    throw new Error(t('error-getting-file'))
                }

                const fileName =
                    attachment?.attachment_file_name ||
                    path.split('/').pop() ||
                    'file'
                downloadFile(presignedUrl, fileName)
            } catch (error) {
                console.error('Failed to download file:', error)
                toast.error(t('error-downloading-file'))
            }
        },
        [getPresignedUrl, t]
    )

    return {
        openFilePreview,
        downloadFileWithPresigned,
        isOfficeFile,
        getFileExtension,
        getPresignedUrl,
        OFFICE_EXTENSIONS,
    }
}
